/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, { ChangeEvent, useMemo, useState } from 'react';
import {
  ICON_CHEVRON_LEFT,
  ICON_CHEVRON_RIGHT,
  ICON_EXPAND_LESS,
  ICON_EXPAND_MORE,
  ICON_FILTER_LIST,
} from '../../Icons';
import { TextField } from '../FormFields/fields/TextField';
import { SdsIcon } from '../SdsIcon';
import { RowConfig, TableCell } from './Table.defs';
import { tableDefaults, TableProps } from './Table.props';

/**
 * SDS Table component.
 */
export const Table = (props: TableProps): JSX.Element => {
  const {
    classes,
    emptyText,
    isPaginated,
    isSearchable,
    isSlim,
    isSortable,
    perPageDefault,
    perPageOptions,
    perPageText,
    searchPlaceholderText,
    tableContent,
    pagination,
    setPagination,
    customFilteringFn,
    ...rest
  } = {
    ...tableDefaults,
    ...props,
  };

  const uniqueId = useUniqueId();
  const tableId = `sds-table--${uniqueId}`;
  const tableClass = clsx(`sds-table__wrapper`, classes, {
    [`table--slim`]: isSlim,
    [`table--sortable`]: isSortable,
  });

  const tableRows = useMemo((): Array<{}> => {
    return tableContent.rows;
  }, [tableContent]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableColumns = useMemo((): Array<ColumnDef<unknown, any>> => {
    return tableContent.columns.map((column: TableCell) => {
      return {
        accessorKey: column.key,
        header: () => column.content,
      };
    });
  }, [tableContent]);

  // To use the table, we need to create a table instance with the options, creating the options object external to the hook allows for optionally passing in pagination state and setter
  const tableInstanceOptions = {
    columns: tableColumns,
    data: tableRows,
    getColumnCanGlobalFilter: () => true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: perPageDefault,
      },
    },
    ...(customFilteringFn && { globalFilterFn: customFilteringFn }),
  } as TableOptions<unknown>;

  // Some components may want to control or use the pagination state externally (e.g. in a parent component) so we optionally allow passing in a pagination state and setter
  if (pagination && setPagination) {
    tableInstanceOptions.state = {};
    tableInstanceOptions.state.pagination = pagination;
    tableInstanceOptions.onPaginationChange = setPagination;
  }

  const tableInstance = useReactTable(tableInstanceOptions);

  const {
    getRowModel,
    getRowCount,
    getHeaderGroups,
    getCanNextPage,
    getCanPreviousPage,
    nextPage,
    previousPage,
    setGlobalFilter,
    setPageSize,
    getState,
  } = tableInstance;
  const {
    globalFilter,
    pagination: { pageIndex, pageSize },
  } = getState();

  const [filterValue, setFilterValue] = useState(globalFilter);

  // Apply table pagination
  let tableFooter = null;

  if (isPaginated) {
    const totalItems = getRowCount();
    const currentItemsBase = pageSize * pageIndex;
    const currentItemsStart = totalItems > 0 ? currentItemsBase + 1 : 0;
    const pageLastRow = currentItemsBase + pageSize;
    // If the page is the last one, show total amount of rows, otherwise show the row range in current page
    const currentItemsFinish = pageLastRow < totalItems ? pageLastRow : totalItems;
    const currentItemsRange = `${currentItemsStart} - ${currentItemsFinish} of ${totalItems}`;

    tableFooter = (
      <div className="sds-table__pagination">
        <div className="sds-table__pagination-options">
          <span className="sds-table__per-page">{perPageText}</span>
          <select
            aria-label="Select number of rows per page"
            className="sds-table__page-select"
            onChange={(e): void => {
              setPageSize(Number(e.target.value));
            }}
            value={pageSize}
          >
            {perPageOptions.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div className="sds-table__navigator">
          <span className="sds-table__row-range">{currentItemsRange}</span>
          <button aria-label="Previous" disabled={!getCanPreviousPage()} onClick={(): void => previousPage()}>
            <SdsIcon svg={ICON_CHEVRON_LEFT} />
          </button>
          <button aria-label="Next" disabled={!getCanNextPage()} onClick={(): void => nextPage()}>
            <SdsIcon svg={ICON_CHEVRON_RIGHT} />
          </button>
        </div>
      </div>
    );
  }

  const handleHeaderKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const header = event.currentTarget as HTMLElement;
      header.click();
    }
  };

  const getTableHeaders = (): Array<JSX.Element> => {
    const headerGroups = getHeaderGroups();
    return headerGroups.map((headerGroup) => {
      return (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            let labelText = 'activate to';

            if (!header.column.getIsSorted()) {
              labelText += ' sort column ascending';
            } else {
              if (header.column.getIsSorted() !== 'desc') {
                labelText += ' sort column descending';
              } else {
                labelText += ' remove sorting';
              }
            }
            return (
              <th
                aria-label={`${header.id}: ${labelText}`}
                aria-sort={
                  header.column.getIsSorted()
                    ? header.column.getIsSorted() === 'desc'
                      ? `descending`
                      : `ascending`
                    : `none`
                }
                key={header.id}
                onClick={(): void => {
                  if (!isSortable) {
                    return;
                  }
                  header.column.toggleSorting();
                }}
                onKeyDown={(event): void => {
                  handleHeaderKeyDown(event);
                }}
                tabIndex={0}
              >
                <div className="sds-table__th-content">
                  {flexRender(header.column.columnDef.header, header.getContext())}

                  {isSortable && header.column.getIsSorted() ? (
                    header.column.getIsSorted() === 'asc' ? (
                      <SdsIcon svg={ICON_EXPAND_LESS} />
                    ) : (
                      <SdsIcon svg={ICON_EXPAND_MORE} />
                    )
                  ) : (
                    <></>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      );
    });
  };

  const getTableRows = (): Array<JSX.Element> => {
    const { rows } = getRowModel();

    if (rows.length === 0) {
      return [
        <tr key={`${tableId}--empty-row`}>
          <td>{emptyText}</td>
        </tr>,
      ];
    }

    return rows.map((row) => {
      // Look for custom properties
      let customClass = ``;
      const originalRows = row.original;
      if (originalRows) {
        if (Object.keys(originalRows).indexOf(`_config`) !== -1) {
          // We know the _config property exists on the provided custom object
          // @ts-ignore TS2339
          const config = originalRows._config as RowConfig;
          customClass = config.classes;
        }
      }
      return (
        <tr className={customClass} key={row.id}>
          {row.getAllCells().map((cell) => {
            return <td key={cell.id}>{cell.renderValue() as number | string | JSX.Element}</td>;
          })}
        </tr>
      );
    });
  };

  const getSearchBox = (): JSX.Element => {
    const filterRows = (event: ChangeEvent): void => {
      const input = event.target as HTMLInputElement;
      setFilterValue(input.value);
      setGlobalFilter(input.value);
    };

    return (
      <TextField
        customChange={filterRows}
        fieldLabel={searchPlaceholderText}
        hideLabel
        icon={ICON_FILTER_LIST}
        id={`${tableId}--filter`}
        name={`${tableId}--filter`}
        placeholder={searchPlaceholderText}
        value={filterValue || ``}
      />
    );
  };

  return (
    <>
      {isSearchable && <div className="sds-table__search">{getSearchBox()}</div>}
      <div className={tableClass} {...rest}>
        <div className="sds-table">
          <table id={tableId}>
            <thead>{getTableHeaders()}</thead>
            <tbody>{getTableRows()}</tbody>
          </table>
        </div>
        {tableFooter}
      </div>
    </>
  );
};
